/**
 * Embed-token manager for `<yom-report>`.
 *
 * WHY THIS EXISTS: YOM embed tokens live ≤ 15 minutes (architecture §3.4).
 * `<yom-report>` only asks its `tokenProvider` when it has no token, and once
 * more after a 401. Left alone, every report on the page would hit a 401 on
 * its first refresh after expiry, and each would mint its own fresh token.
 * This manager:
 *   • caches ONE bundle per page (one backend call feeds every report),
 *   • de-duplicates concurrent fetches (eight reports mounting at once = one call),
 *   • refreshes on a timer BEFORE expiry and tells subscribers, so reports
 *     swap tokens without ever seeing a 401,
 *   • treats a call that hands back the token we already gave out as "that
 *     token was rejected" and forces a fresh one (the element's 401 path).
 *
 * Pure: no React, no DOM, injectable clock and timers, so it is unit-tested.
 */
import type { TokenBundle } from './types';

export interface TokenManagerOptions {
  /** Fetches a fresh bundle from the Tyashin backend. */
  fetchBundle: () => Promise<TokenBundle>;
  /** Refresh this long before expiry. Default 60 s. */
  refreshSkewMs?: number;
  /** Floor for the refresh delay, so a nearly-expired token can't spin. Default 5 s. */
  minDelayMs?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface GetTokenOptions {
  /**
   * The token the caller currently holds. When it equals the cached token the
   * caller is reporting a rejection (401), so the cache is skipped.
   */
  rejected?: string | null;
}

export interface EmbedTokenManager {
  /** Current bundle, fetching (or refreshing) when needed. */
  getBundle(opts?: GetTokenOptions & { source?: string }): Promise<TokenBundle>;
  /** Token for one source (e.g. 'site', 'app', 'sites'). */
  getToken(source: string, opts?: GetTokenOptions): Promise<string>;
  /** Called with each new bundle after a refresh. Returns an unsubscribe. */
  subscribe(listener: (bundle: TokenBundle) => void): () => void;
  /** Last bundle, without fetching. */
  peek(): TokenBundle | null;
  /** Stop timers and drop listeners. */
  dispose(): void;
}

export const DEFAULT_REFRESH_SKEW_MS = 60_000;
const DEFAULT_MIN_DELAY_MS = 5_000;

export function createEmbedTokenManager(options: TokenManagerOptions): EmbedTokenManager {
  const skew = options.refreshSkewMs ?? DEFAULT_REFRESH_SKEW_MS;
  const minDelay = options.minDelayMs ?? DEFAULT_MIN_DELAY_MS;
  const now = options.now ?? (() => Date.now());
  const setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer =
    options.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));

  let bundle: TokenBundle | null = null;
  let inflight: Promise<TokenBundle> | null = null;
  let timer: unknown = null;
  let disposed = false;
  const listeners = new Set<(b: TokenBundle) => void>();

  const isFresh = (b: TokenBundle | null): b is TokenBundle =>
    Boolean(b) && (b as TokenBundle).expiresAt - skew > now();

  function schedule(b: TokenBundle) {
    if (timer !== null) clearTimer(timer);
    timer = null;
    if (disposed) return;
    const delay = Math.max(minDelay, b.expiresAt - skew - now());
    timer = setTimer(() => {
      timer = null;
      // Background refresh: on failure keep the old bundle; the element's own
      // 401 path will ask again, and the next getBundle retries the fetch.
      void refresh().catch(() => {});
    }, delay);
  }

  function refresh(): Promise<TokenBundle> {
    if (inflight) return inflight;
    const hadBundle = bundle !== null;
    inflight = options
      .fetchBundle()
      .then((fresh) => {
        if (disposed) return fresh;
        bundle = fresh;
        schedule(fresh);
        if (hadBundle) listeners.forEach((l) => l(fresh));
        return fresh;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  }

  function wasRejected(source: string | undefined, rejected: string | null | undefined): boolean {
    if (!rejected || !bundle) return false;
    if (source) return bundle.sources[source]?.token === rejected;
    return Object.values(bundle.sources).some((s) => s.token === rejected);
  }

  return {
    async getBundle(opts = {}) {
      if (isFresh(bundle) && !wasRejected(opts.source, opts.rejected)) return bundle;
      return refresh();
    },
    async getToken(source, opts = {}) {
      const b = await this.getBundle({ ...opts, source });
      const src = b.sources[source];
      if (!src) throw new Error(`No analytics token for "${source}"`);
      return src.token;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    peek: () => bundle,
    dispose() {
      disposed = true;
      if (timer !== null) clearTimer(timer);
      timer = null;
      listeners.clear();
    },
  };
}
